import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { BsInfoCircle, BsLock } from 'react-icons/bs';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Drawer, DrawerBody } from '../../../../v2/js/components/drawer';
import { StatusCodes } from '../../../../v2/js/constants/workflows';
import { useRequireActingAsEntity } from '../../../../v2/js/hooks/useRequireActingAsEntity';
import { useStaffOrHasPermission } from '../../../../v2/js/hooks/useStaffOrHasPermission';
import { ConnectionDrawer } from '../../../../v2/js/pages/Organization/Connections/ConnectionDrawer';
import { TRANSITION_ALL } from '../../../../v2/js/utils/transitionStyles';
import { ConnectionsService, ProviderService } from '../../../api';
import { Card } from '../../../app/components/Card';
import { ConnectedFacilitiesList } from '../../../app/components/ConnectedFacilitiesList';
import { ProviderProfileHeader } from '../../../app/components/ProviderProfileHeader';
import { TabsNavbar } from '../../../app/components/TabsNavbar';

import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { connectionRequestsQueryKeys } from '../../../app/query-keys-factories/connection-requests';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { CreateConnectionDialog } from './CreateConnectionDialog';

const ConnectButton = ({
  isLoading,
  hasConnection,
  onCreateConnection,
  onAddFacility,
  providerId,
  availabilityToken,
  allowCreateAssignment = false,
}: {
  isLoading: boolean;
  hasConnection: boolean;
  onCreateConnection: () => void;
  onAddFacility: () => void;
  providerId: string;
  availabilityToken?: string | null;
  allowCreateAssignment?: boolean;
}) => {
  const hasCreatePermission = useStaffOrHasPermission('create__assignments__assignment');
  const hasConnectPermission = useStaffOrHasPermission('create__connections__connectedfacility');
  const requireActingAs = useRequireActingAsEntity();
  const navigate = useNavigate();

  if (!hasConnectPermission || isLoading) {
    return null;
  }

  const canCreateAssignment = allowCreateAssignment && hasCreatePermission;
  const returnUrl = `/app/healthcare-entity/providers/${providerId}`;
  const tokenParam = availabilityToken ? `&token=${availabilityToken}` : '';
  const createAssignmentUrl = `/app/organization/assignments/create?provider=${providerId}${tokenParam}&returnUrl=${encodeURIComponent(returnUrl)}`;

  return (
    <Col
      className="d-flex gap-2 align-items-center justify-content-end"
      sm={{ order: 1, span: 12 }}
      md={{ order: 2, span: 'auto' }}
    >
      {canCreateAssignment && (
        <Button
          variant="outline-primary"
          onClick={() => requireActingAs(() => navigate(createAssignmentUrl))}
        >
          New Assignment
        </Button>
      )}
      {hasConnection ? (
        <Button onClick={() => requireActingAs(onAddFacility)}>Connect</Button>
      ) : (
        <Button onClick={() => requireActingAs(onCreateConnection)}>Connect</Button>
      )}
    </Col>
  );
};

const ProviderTabs = ({
  provider,
  providerPermissions,
}: {
  provider: any;
  providerPermissions: any;
}) => (
  <TabsNavbar
    alignment="center"
    tabs={[
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Credentials</>,
        to: `/app/healthcare-entity/providers/${provider.id}/credentials`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Professional Traits</>,
        to: `/app/healthcare-entity/providers/${provider.id}/professional-traits`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Education</>,
        to: `/app/healthcare-entity/providers/${provider.id}/education`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: (
          <>{!providerPermissions?.retrieve.details && <BsLock />} Licenses & Certifications</>
        ),
        to: `/app/healthcare-entity/providers/${provider.id}/licenses`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Other</>,
        to: `/app/healthcare-entity/providers/${provider.id}/other`,
        disabled: !providerPermissions?.retrieve.details,
      },
    ]}
  />
);

export const ProviderProfile = () => {
  useDocumentTitle('Provider Profile');
  const { providerId: providerIdOrUndefined } = useParams();
  const navigate = useNavigate();

  const [showCreateConnectionDialog, setShowCreateConnectionDialog] = useState(false);
  const [showAddFacilityConnectionDialog, setShowAddFacilityConnectionDialog] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  const providerId = providerIdOrUndefined ?? '';

  const {
    data: providerPermissions,
    isLoading: isProviderPermissionsLoading,
    error: permissionsFetchError,
  } = useQuery({
    queryKey: providerQueryKeys.permissions(providerId),
    queryFn: async () =>
      await ProviderService.providerPermissionsRetrieve({
        providerId,
      }),
    enabled: !!providerId,
  });

  const {
    isLoading: isProviderLimitedLoading,
    error: providerLimitedFetchError,
    data: providerLimited,
  } = useQuery({
    queryKey: providerQueryKeys.limitedDetail(providerId),
    queryFn: async () =>
      await ProviderService.providerLimitedRetrieve({
        providerId: providerId,
      }),
    enabled:
      providerPermissions?.retrieve.details === false &&
      providerPermissions?.retrieve.limited_details,
  });

  const {
    isLoading: isProviderFullLoading,
    error: providerFullFetchError,
    data: providerFull,
  } = useQuery({
    queryKey: providerQueryKeys.detail(providerId),
    queryFn: async () =>
      await ProviderService.providerRetrieve({
        providerId: providerId,
      }),
    enabled: providerPermissions?.retrieve.details,
  });

  const connectionQueryParams: Parameters<
    typeof ConnectionsService.connectionsConnectedFacilityList
  >[0] = {
    provider: providerId,
    ordering: 'facility__name',
  };
  const { data: connectionData, isLoading: isConnectionDataLoading } = useQuery({
    queryKey: connectionRequestsQueryKeys.list(connectionQueryParams),
    queryFn: async () =>
      await ConnectionsService.connectionsConnectedFacilityList(connectionQueryParams),
    enabled: !!providerId,
  });

  const provider = providerPermissions?.retrieve.details ? providerFull : providerLimited;
  const isProviderLoading = providerPermissions?.retrieve.details
    ? isProviderFullLoading
    : isProviderLimitedLoading;
  const providerFetchError = providerPermissions?.retrieve.details
    ? providerFullFetchError
    : providerLimitedFetchError;

  const hasConnection = useMemo(() => {
    if (isConnectionDataLoading || !connectionData?.results) {
      return false;
    }
    return connectionData.results.length > 0;
  }, [connectionData?.results, isConnectionDataLoading]);

  useEffect(() => {
    if (!providerIdOrUndefined) {
      navigate('/app');
    }
  }, [providerIdOrUndefined, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // const queryClient = useQueryClient();
  // const invalidateQueries = useCallback(async () => {
  //   await Promise.all([
  //     queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists()),
  //     queryClient.invalidateQueries(providerQueryKeys.allLists()),
  //     queryClient.invalidateQueries(providerQueryKeys.allDetails()),
  //     queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists()),
  //   ]);
  // }, [queryClient, providerId]);

  return (
    <>
      <div
        className={clsx(
          `tw:flex tw:min-h-0 ${TRANSITION_ALL}`,
          selectedConnectionId && 'tw:h-[calc(100vh-72px)] tw:overflow-hidden'
        )}
      >
        <div
          className={clsx(
            `tw:flex-1 tw:min-w-0 ${TRANSITION_ALL}`,
            selectedConnectionId
              ? 'tw:w-3/5 tw:lg:w-3/5 tw:overflow-y-auto'
              : 'tw:w-full tw:overflow-hidden'
          )}
        >
          <div style={{ position: 'relative' }}>
            <div
              className="bg-primary-dark d-block"
              style={{
                height: 'calc(3rem + 120px)',
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                zIndex: -10,
              }}
            />
            <Container>
              <Row>
                <Col xs={12}>
                  {!isProviderLoading &&
                    !isProviderPermissionsLoading &&
                    (!!providerFetchError || !!permissionsFetchError) && (
                      <Row className="row-cols-auto justify-content-center">
                        <Col>
                          <Alert variant="danger" show={!!providerFetchError}>
                            <Alert.Heading>Something went wrong</Alert.Heading>
                            <p>
                              Please try again later. If the problem persists, please contact us.
                            </p>
                          </Alert>
                        </Col>
                      </Row>
                    )}
                  <ProviderProfileHeader
                    provider={provider}
                    isProviderLoading={isProviderPermissionsLoading || isProviderLoading}
                    hasAcceptedConnection={
                      (provider?.clearance_status &&
                        provider?.clearance_status !== StatusCodes.NOT_CLEARED) ||
                      false
                    }
                    callToAction={
                      <Row className="h-100 d-flex gap-3 align-items-center justify-content-xl-end pb-3">
                        <ConnectButton
                          isLoading={isProviderLoading || isProviderPermissionsLoading}
                          hasConnection={hasConnection ?? false}
                          onCreateConnection={() => setShowCreateConnectionDialog(true)}
                          onAddFacility={() => setShowAddFacilityConnectionDialog(true)}
                          providerId={providerId}
                          availabilityToken={provider?.availability_token}
                          allowCreateAssignment={!!providerPermissions?.retrieve.details}
                        />
                      </Row>
                    }
                  />
                  {!!provider &&
                    !isProviderPermissionsLoading &&
                    providerPermissions?.retrieve.limited_details && (
                      <ConnectedFacilitiesList
                        providerId={provider.id}
                        onConnectionSelect={setSelectedConnectionId}
                      />
                    )}
                  <Card className="mb-4">
                    {(isProviderLoading || isProviderPermissionsLoading) && (
                      <Row>
                        <Col xs={12} className="d-flex align-items-center justify-content-center">
                          <Spinner
                            aria-label="loading indicator"
                            animation="grow"
                            className="loading-spinner"
                            variant="primary"
                          />
                        </Col>
                      </Row>
                    )}

                    {!!provider && providerPermissions?.retrieve.details && (
                      <>
                        <ProviderTabs
                          provider={provider}
                          providerPermissions={providerPermissions}
                        />
                        <div className="d-flex flex-column position-relative p-4">
                          <Outlet />
                        </div>
                      </>
                    )}
                    {!!provider && !providerPermissions?.retrieve.details && (
                      <div className="d-flex flex-column align-items-center justify-content-center text-center p-5">
                        <BsInfoCircle size={48} className="mb-3" />
                        <div className="fs-4 mb-3">
                          You must be connected with a Provider to view their details
                        </div>
                        <Row className="justify-content-center">
                          <ConnectButton
                            isLoading={isProviderLoading || isProviderPermissionsLoading}
                            hasConnection={hasConnection ?? false}
                            onCreateConnection={() => setShowCreateConnectionDialog(true)}
                            onAddFacility={() => setShowAddFacilityConnectionDialog(true)}
                            providerId={providerId}
                            availabilityToken={provider?.availability_token}
                            allowCreateAssignment={false}
                          />
                        </Row>
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
        </div>

        {selectedConnectionId && providerPermissions?.retrieve.limited_details && (
          <Drawer
            open={!!selectedConnectionId}
            onClose={() => setSelectedConnectionId(null)}
            width="3xl"
            mode="side-by-side"
            className={TRANSITION_ALL}
          >
            <DrawerBody className="tw:!text-sm/6">
              <ConnectionDrawer
                connectionId={selectedConnectionId}
                onClose={() => setSelectedConnectionId(null)}
              />
            </DrawerBody>
          </Drawer>
        )}
      </div>
      {!isConnectionDataLoading && !!provider && (
        <>
          <CreateConnectionDialog
            provider={provider}
            isDialogOpen={showCreateConnectionDialog}
            closeDialog={() => setShowCreateConnectionDialog(false)}
            hasConnection={false}
          />
          <CreateConnectionDialog
            provider={provider}
            isDialogOpen={showAddFacilityConnectionDialog}
            closeDialog={() => setShowAddFacilityConnectionDialog(false)}
            hasConnection={true}
          />
        </>
      )}
    </>
  );
};
